/**
 * Step 4 — Drop slots (Phase 21): one list of drops, all groups together —
 * Slots (set how many in one go) and Every (the same field as Schedule ›
 * Every, 20/10/2026: "make it modulable directly here"), the list (drag to move, chips pick the groups, a pin keeps a time), then
 * Add / Fill and the quick fills. Groups are separate: a group with nothing
 * to send today has its drops skipped (said under two groups or more). Runtime rows (smart/probe) belong to
 * the optimizer — never shown here.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from "react";

import { useDayPreview } from "../../../useDropGenerator";
import type { SlotTime } from "../../../utils/slot-times";
import { capCutsOf } from "../../shared/cap/cap-cuts";
import type { ContainerEditor } from "../state/useContainerEditor";
import type { GroupRef } from "./hooks/use-general-times";
import type { OutsideRemovalModel } from "./hooks/use-outside-removal";
import { EveryRow } from "./schedule/EveryRow";
import { dropRows } from "./slots/list/drop-list-order";
import { DropList } from "./slots/list/DropList";
import { DropListFooter } from "./slots/list/DropListFooter";
import { CapLine } from "./slots/cap/CapLine";
import { SlotCountRow } from "./slots/count/SlotCountRow";
import { DropRankSlots } from "./slots/rank/DropRankSlots";

export interface SlotsStepProps {
  editor: ContainerEditor;
  countryKey?: string;
  containerId?: string | null;
  /** Planned time per slot key (slotKey). */
  timesByKey: Map<string, SlotTime>;
  /** Linked groups in link order. */
  groupRefs: GroupRef[];
  /** Resolved container start (chain-aware) — null hides Fill. */
  startMin: number | null;
  /** The end time on the country's clock — the Fill boundary; null = none. */
  endMin?: number | null;
  /** Country window end — the Fill boundary when no end time is set. */
  windowEndMin: number;
  /** "Delete the drops outside the window" (19/10/2026); absent = no button. */
  outside?: OutsideRemovalModel | null;
}

const EVERY_HINT =
  'Minutes between two drops on the clock — the same setting as Schedule › Every. Change it here and the list re-times at once; a pinned drop keeps its own time.';

export const SlotsStep: FC<SlotsStepProps> = ({ editor, countryKey, timesByKey, groupRefs, startMin, endMin = null, windowEndMin, outside, containerId = null }) => {
  const timeOrder = dropRows(editor.draft.slots, timesByKey).map((r) => r.index);
  const cuts = capCutsOf(useDayPreview(countryKey ?? null).data?.warnings, containerId); // the slots the daily cap cut today (20/10/2026)
  return (
    <div className="flex flex-col gap-4">
      <div className="divide-y divide-border/50">
        <SlotCountRow editor={editor} timeOrder={timeOrder} />
        <EveryRow editor={editor} hint={EVERY_HINT} />
      </div>
      <CapLine cuts={cuts} />
      <p className="text-[11px] text-muted-foreground">
        The drops share that clock in the order you drag them; a pinned drop keeps its own time. Click a list to choose it directly, its
        group to change membership, or its gateway to choose the gateway and SID. &ldquo;auto&rdquo; = it follows the container&apos;s rule, shown as it lands today.
        {groupRefs.length > 1 ? ' Each group is separate: on a day a group has nothing left to send, its drops are skipped.' : null}
      </p>
      <DropList countryKey={countryKey} containerId={containerId} editor={editor} timesByKey={timesByKey} groupRefs={groupRefs} cuts={cuts} />
      <DropListFooter editor={editor} startMin={startMin} endMin={endMin} windowEndMin={windowEndMin} groupCount={groupRefs.length} timeOrder={timeOrder} outside={outside} />
      <DropRankSlots groupRefs={groupRefs} editor={editor} />
    </div>
  );
};
